import { useEffect, useState } from "react";

/**
 * Reactive network connectivity (issue #77).
 *
 * Initializes from `navigator.onLine` and subscribes to the window's
 * `online` / `offline` events. One shared hook; the offline banner, the
 * offline-aware error surfaces, and the booking-action guards all read
 * the same boolean, so the whole app flips state together.
 *
 * Note: `navigator.onLine` reflects the network link, not server
 * reachability — good enough for a best-effort signal.
 */
export function useOnline(): boolean {
  const [online, setOnline] = useState<boolean>(() => navigator.onLine);

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);

    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);

    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  return online;
}
